import { useEffect, useRef, useState, type ReactNode } from "react";

export const CANVAS_LOADING_LOGO_SRC = "/meiden-connect-logo-animation.gif";

type LoadingLogoState = "pending" | "ready" | "failed";

interface LoadingOverlayProps {
  readonly title?: string | undefined;
  readonly message: string;
  readonly progress: number;
  readonly variant?: "loading" | "error" | undefined;
  // エラー時にメッセージ下へ差し込む追加要素（自動リロードのカウントダウンなど）。
  readonly footer?: ReactNode | undefined;
}

export function LoadingOverlay({
  title = "読み込み中です",
  message,
  progress,
  variant = "loading",
  footer,
}: LoadingOverlayProps): JSX.Element {
  const safeProgress = Math.min(100, Math.max(0, Math.round(progress)));
  const isError = variant === "error";
  const logoRef = useRef<HTMLImageElement>(null);
  const [logoState, setLogoState] = useState<LoadingLogoState>("pending");

  // キャッシュ済みの画像では、React がイベントハンドラーを設定する前に読み込みが
  // 完了していることがあるため、マウント後に結果を補完する。
  useEffect(() => {
    const image = logoRef.current;
    if (image === null || !image.complete) {
      return;
    }
    setLogoState(image.naturalWidth > 0 ? "ready" : "failed");
  }, []);

  return (
    <div
      className="loading-overlay"
      data-testid="loading-overlay"
      role={isError ? "alert" : "status"}
      aria-live={isError ? "assertive" : "polite"}
      aria-busy={!isError}
    >
      <div className="loading-overlay-panel">
        {isError ? (
          <span className="loading-overlay-error-mark" aria-hidden="true">
            !
          </span>
        ) : (
          <span className="loading-overlay-mark" data-logo-state={logoState} aria-hidden="true">
            <img
              ref={logoRef}
              className="loading-overlay-logo"
              src={CANVAS_LOADING_LOGO_SRC}
              alt=""
              width={400}
              height={200}
              decoding="async"
              onLoad={() => setLogoState("ready")}
              onError={() => setLogoState("failed")}
            />
            <span className="loading-overlay-spinner" />
          </span>
        )}
        <div className="loading-overlay-copy">
          <p className="loading-overlay-title">{title}</p>
          <p className="loading-overlay-message">{message}</p>
        </div>
        <div className="loading-overlay-progress" aria-label={`読み込み進捗 ${safeProgress}%`}>
          <span style={{ width: `${safeProgress}%` }} />
        </div>
        <span className="loading-overlay-percent">{safeProgress}%</span>
        {footer !== undefined ? <div className="loading-overlay-footer">{footer}</div> : null}
      </div>
    </div>
  );
}
